import React, { useEffect, useState } from "react";
import { Phone, MoveRight, Download, Loader } from "lucide-react";
import PrimaryButton from "../common/buttons/PrimaryButton";
import Div100vh from "react-div-100vh";
import BorderImg from "@/assets/images/border-box.png";
import LineLeft from "@/assets/icons/line-left.png";
import LineRight from "@/assets/icons/line-right.png";
import { trackBtnClick, updateFunction, handleAndTrackError } from "@/lib/helper";
import { fetchData } from "@/lib/helper";
import { useToast } from "@/components/ui/use-toast";

function ButtonCustom({
  heading,
  subHeading,
  ctaText,
  handleButton,
  btnIcon: Icon,
  isLoading,
}) {
  return (
    <div className="relative flex flex-col gap-2 border border-[#5c5c5c] px-4 py-2">
      {ctaText === "Request Callback" && (
        <span className="absolute -top-2 -left-2 bg-[#010101] px-2 text-3xs text-[#12c06c] font-medium">
          Preferred by most tenants
        </span>
      )}
      <div className="flex flex-col gap-1">
        <span className="text-[#cccccc] text-sm font-medium leading-snug">
          {heading}
        </span>
        <span className="text-gray-500 text-2xs">{subHeading}</span>
      </div>
      <div className="flex justify-end items-center">
        <div class="max-w-md rounded-3xl p-px bg-gradient-to-b from-[#FFFFFF] via-[#010101] to-[#999999] dark:from-blue-800 dark:to-purple-800 ">
          <button
            className={`flex items-center gap-2 w-fit rounded-full px-6 py-2 shadow-md transition ${
              ctaText === "Connect"
                ? "bg-gradient-to-r from-[rgba(1,1,1,0.8)] via-[#12c06c] to-[#12c06c] text-white hover:opacity-90"
                : isLoading
                ? "bg-gray-100 cursor-not-allowed"
                : "bg-white hover:bg-gray-100"
            }`}
            onClick={handleButton}
            disabled={isLoading} // Disable button during loading state
          >
            <span
              className={`text-black text-2xs font-medium ${
                ctaText === "Connect" ? "text-white" : "text-black"
              }`}
            >
              {ctaText}
            </span>
            {Icon && (
              <Icon
                className={`w-3 h-3 ${
                  ctaText === "Connect" ? "text-white" : "text-black"
                }`}
              />
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

function DownloadOfferButton() {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [landlordData, setLandlordData] = useState(false);

  console.log(landlordData);

  const getLandlord = async () => {
    const data = await fetchData("user/my-landlords");
    setLandlordData(data);
  };

  useEffect(() => {
    getLandlord();
  }, []);

  const downloadBrochure = async () => {
    try {
      setIsLoading(true); // Set loading state

      // Fetch the response from the API
      await fetchData(
        `landlord/generate-offer/${landlordData?.[0]?.landlordId}`
      );

      toast({
        title: "Landlord Offer Sent at Whatsapp",
      });
    } catch (error) {
      // Handle error
      handleAndTrackError(error, {
        context: "Downloading Landlord Offer",
        endpoint: "landlord/generate-offer",
      });
    } finally {
      setIsLoading(false); // Reset loading state
    }
  };

  return (
    <div className="relative min-h-52 flex flex-col gap-2 items-center p-6 overflow-hidden">
      {/* Gradient Border */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          zIndex: 1,
          background: `
                linear-gradient(to right, rgba(255, 255, 255, 0), rgba(255, 255, 255, 0.7), rgba(255, 255, 255, 0)) top,
                linear-gradient(to right, rgba(255, 255, 255, 0), rgba(255, 255, 255, 0.7), rgba(255, 255, 255, 0)) bottom,
      linear-gradient(to bottom, rgba(255, 255, 255, 0), rgba(255, 255, 255, 0.7), rgba(255, 255, 255, 0)) right,
      linear-gradient(to bottom, rgba(255, 255, 255, 0), rgba(255, 255, 255, 0.7), rgba(255, 255, 255, 0)) left
                `,
          backgroundSize: "100% 0.8px, 100% 0.8px, 0.8px 100%, 0.8px 100%",
          backgroundRepeat: "no-repeat",
        }}
      />

      {/* Background Image */}
      <div
        className="absolute inset-0 pointer-events-none rounded-lg -mx-12"
        style={{
          zIndex: 0,
          backgroundImage: `url(${BorderImg})`,
          backgroundSize: "cover",
          backgroundRepeat: "no-repeat",
          backgroundPosition: "center",
        }}
      />

      {/* Content */}
      <div className="flex flex-col relative z-10 gap-6 items-center justify-center">
        <div className="flex flex-col gap-2">
          <span className="bg-gradient-to-r font-medium from-[#888888] text-xl via-white to-[#888888] bg-clip-text text-transparent">
            CONGRATULATIONS
          </span>
          <span className="text-xs">
            Extend the customised club offer to your landlord
          </span>
        </div>
        <div className="w-full flex items-center justify-end">
          <button
            onClick={downloadBrochure}
            disabled={isLoading} // Disable the button when loading
            className={`min-w-56 min-h-12 py-3 px-6 flex text-2xs gap-2 font-semibold items-center justify-center rounded-full transition ${
              isLoading
                ? "bg-[#0032fb] opacity-50 cursor-not-allowed"
                : "bg-[#0032fb] bg-gradient-to-r from-[#00000000] via-[#2C55FF] to-[#00000000] hover:bg-[#0030e5]"
            }`}
          >
            {isLoading ? (
              <>
                <span>Generating Offer...</span>
                <Loader className="w-4 h-4 animate-spin" />{" "}
              </>
            ) : (
              <>
                <span>Download Landlord Offer</span>
                <Download className="w-3" />
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function ViewLandlordOffer() {
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  const requestCallback = async () => {
    try {
      setIsLoading(true);

      await updateFunction(
        { type: "REQUEST_CALLBACK" },
        "user/request-callback",
        "request_callback",
        "View Offer Screen"
      );
      toast({
        title: "Callback Requested Successfully",
      });
    } catch (error) {
      // Handle error
      handleAndTrackError(error, {
        context: "Request Callback Button",
        endpoint: "user/request-callback",
      });
    } finally {
      setIsLoading(false); // Reset loading state
    }
  };

  const requestConnect = async () => {
    try {
      setIsLoading(true);

      await updateFunction(
        { type: "CONNECTED" },
        "user/request-callback",
        "connect",
        "View Offer Screen"
      );
      // toast({
      //   title: "Callback Requested Successfully",
      // });
    } catch (error) {
      // Handle error
      handleAndTrackError(error, {
        context: "Connect Button",
        endpoint: "user/request-callback",
      });
    } finally {
      setIsLoading(false); // Reset loading state
    }
  };

  return (
    <Div100vh className="px-6 py-12 flex flex-col gap-6 justify-between">
      <DownloadOfferButton />
      <div className="flex flex-col gap-8">
        <div className="flex flex-col gap-4">
          <div className="flex items-center w-full gap-4 justify-center flex-nowrap">
            <img src={LineLeft} alt="" className="max-w-12" />
            <span className="text-[#d3d3d3] text-xs font-medium whitespace-nowrap">
              How do you want to proceed?
            </span>
            <img src={LineRight} alt="" className="max-w-12" />
          </div>

          <ButtonCustom
            heading="Speak to CirclePe Team"
            subHeading="To understand Landlord's offer & any questions"
            ctaText="Connect"
            btnIcon={Phone} // Pass Phone as the component
            handleButton={() => {
              requestConnect();
              window.location.href = "tel:+918368376123";
            }} // Replace with the actual phone number
          />

          <ButtonCustom
            heading="Let us extend the offer to your landlord"
            subHeading="Our expert will connect with you & then explain the offer to your landlord on your behalf"
            ctaText="Request Callback"
            handleButton={() => {
              trackBtnClick("Request Callback", "View Landlord Offers Screen");
              requestCallback();
            }}
            isLoading={isLoading}
          />
        </div>
      </div>
      <PrimaryButton
        text="Back to Home"
        handleClick={() => {
          window.location.href = "/";
        }}
      />
    </Div100vh>
  );
}
